import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FileText, Clock, AlertCircle, CheckCircle2, ArrowRight,
  Car, Plane, Building2, ShoppingBag, Upload, Download,
} from 'lucide-react';
import CorporateNavbar from '@/components/CorporateNavbar';
import CorporateFooter from '@/components/CorporateFooter';
import LightPageHero from '@/components/LightPageHero';

const problems = [
  { icon: AlertCircle, text: 'Du vet inte hur du ska formulera dig' },
  { icon: Clock,       text: 'Det tar tid att skriva ett bra brev' },
  { icon: FileText,    text: 'Du missar viktiga detaljer och lagstöd' },
];

const solutions = [
  { icon: CheckCircle2, text: 'Guidat formulär — du svarar på frågor, AI skriver brevet' },
  { icon: CheckCircle2, text: 'Professionell ton och rätt formalia direkt' },
  { icon: CheckCircle2, text: 'Klart att skicka — ladda ner som PDF' },
];

const useCases = [
  { icon: Car,       label: 'Parkeringsböter',    desc: 'Överklaga felaktiga p-böter och bestrida avgifter.' },
  { icon: Plane,     label: 'Flygförseningar',     desc: 'Kräv kompensation enligt EU-förordning 261/2004.' },
  { icon: Building2, label: 'Myndigheter',         desc: 'Överklaga beslut från Försäkringskassan, CSN m.fl.' },
  { icon: ShoppingBag, label: 'Företag & butiker', desc: 'Reklamationer, garantiärenden och felaktiga fakturor.' },
];

const steps = [
  { num: '01', icon: FileText, title: 'Fyll i ärendet', desc: 'Beskriv vad som hänt — ett enkelt formulär guidar dig.' },
  { num: '02', icon: Upload,   title: 'Ladda upp underlag', desc: 'Kvitton, foton eller andra dokument som stärker ditt ärende.' },
  { num: '03', icon: Download, title: 'Ladda ner PDF', desc: 'AI skriver brevet. Du granskar och skickar.' },
];

const KlagobotPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Helmet>
        <title>Klagobot — Skriv klagomål med AI på sekunder</title>
        <meta name="description" content="Klagobot hjälper dig skriva effektiva klagomål till företag och myndigheter. AI-genererade brev som faktiskt ger resultat. Prova gratis." />
        <meta property="og:title" content="Klagobot — AI-driven klagomålshantering" />
        <meta property="og:description" content="Skriv klagomål som faktiskt ger svar. AI formulerar ditt ärende professionellt på sekunder." />
        <meta property="og:url" content="https://klagobot.se" />
        <link rel="canonical" href="https://klagobot.se" />
      </Helmet>
      <CorporateNavbar />

      <LightPageHero
        badge="Klagobot"
        heading={<>Skapa reklamationer och överklaganden<br /><span style={{ background: 'linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #60a5fa 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>på några minuter</span></>}
        subtext="Slipp formulera själv. Beskriv ditt ärende — Klagobot skriver ett färdigt, professionellt brev direkt."
        ctaLabel="Testa Klagobot direkt →"
        ctaHref="https://klagobot.se"
        ctaNote="Tar 2 minuter"
      />

      {/* ── Trust strip ── */}
      <div className="bg-white border-b border-border-light py-4">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
            {[
              { icon: '⏱', text: 'Färdigt brev på 2–5 minuter' },
              { icon: '⚖️', text: 'Bygger på svensk konsumenträtt' },
              { icon: '📎', text: 'Används för reklamationer och överklaganden' },
              { icon: '📥', text: 'Kan laddas ner som PDF' },
            ].map(({ icon, text }) => (
              <div key={text} className="flex items-center gap-2">
                <span className="text-base" aria-hidden="true">{icon}</span>
                <span className="text-sm font-medium text-text-secondary">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Så ser resultatet ut ── */}
      <section className="py-16 bg-surface-subtle border-b border-border-light">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-2xl mx-auto text-center"
          >
            <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-4">Resultatet</span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-text-primary tracking-tight mb-10">
              Så ser resultatet ut
            </h2>

            {/* Mock letter preview card */}
            <div className="bg-white rounded-2xl border border-border-light shadow-lg overflow-hidden text-left">
              {/* Card header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-border-light bg-surface-subtle">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-brand-indigo" />
                  <span className="text-xs font-semibold text-text-secondary">klagomal_parkeringsbot.pdf</span>
                </div>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                  Redo att skickas direkt
                </span>
              </div>

              {/* Realistic letter content */}
              <div className="px-6 py-5 font-mono text-[12px] leading-relaxed text-slate-700 space-y-2">
                <p className="font-semibold text-slate-900">Anna Lindström</p>
                <p className="text-slate-500">Storgatan 12, 112 34 Stockholm · anna.lindstrom@email.se</p>
                <div className="h-px bg-border-light my-3" />
                <p className="font-semibold text-slate-900">Bestridande av kontrollavgift — ref. 2024-04817</p>
                <p className="mt-2 text-slate-600">
                  Jag bestrider kontrollavgiften utfärdad den 8 april 2024 på Parkgatan 3 i Stockholm (reg.nr ABC 123).
                </p>
                <p className="text-slate-600">
                  Parkeringen var enligt skyltningen tillåten mellan 08:00–18:00 på vardagar. Händelsen inträffade en lördag kl. 14:22, vilket innebär att parkeringen var fullt tillåten enligt gällande regler.
                </p>
                <p className="text-slate-600">
                  Bifogat finner ni foto av skylten taget vid tillfället samt fordonets plats. Jag yrkar på att kontrollavgiften omedelbart makuleras i enlighet med parkeringslagen (1993:1parking).
                </p>
                <div className="h-px bg-border-light my-3" />
                <p className="font-semibold text-indigo-700">Med vänlig hälsning,</p>
                <p className="text-slate-700">Anna Lindström</p>
              </div>
            </div>

            <div className="flex items-center justify-center gap-4 mt-4 flex-wrap">
              <p className="text-xs text-text-muted">Genereras automatiskt baserat på ditt ärende</p>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full border border-indigo-200">
                Kan laddas ner som PDF
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Problem ── */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="text-center mb-14"
            >
              <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-4">Problemet</span>
              <h2 className="text-3xl sm:text-[40px] font-extrabold text-text-primary tracking-[-0.02em] leading-tight">
                Att skriva klagomål är svårt
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {problems.map(({ icon: Icon, text }, i) => (
                <motion.div
                  key={text}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="flex gap-4 items-start bg-red-50 rounded-2xl border border-red-100 p-6"
                >
                  <Icon className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                  <p className="text-[15px] text-text-primary font-medium leading-snug">{text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Lösning ── */}
      <section className="py-20 bg-surface-subtle border-t border-border-light">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="text-center mb-14"
            >
              <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-4">Lösningen</span>
              <h2 className="text-3xl sm:text-[40px] font-extrabold text-text-primary tracking-[-0.02em] leading-tight">
                Klagobot gör jobbet
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {solutions.map(({ icon: Icon, text }, i) => (
                <motion.div
                  key={text}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="flex gap-4 items-start bg-white rounded-2xl border border-border-light p-6 shadow-sm"
                >
                  <Icon className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
                  <p className="text-[15px] text-text-primary font-medium leading-snug">{text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Användningsområden ── */}
      <section className="py-20 bg-white border-t border-border-light">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-14"
          >
            <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-4">Användningsområden</span>
            <h2 className="text-3xl font-extrabold text-text-primary tracking-tight">
              Vad kan du klaga på?
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-4xl mx-auto">
            {useCases.map(({ icon: Icon, label, desc }, i) => (
              <motion.div
                key={label}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                className="bg-white rounded-2xl border border-border-light p-6 text-center shadow-sm hover:shadow-lg hover:border-indigo-200 transition-all duration-300"
              >
                <div className="inline-flex w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 items-center justify-center mb-4 shadow-md">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-[15px] font-bold text-text-primary mb-2">{label}</h3>
                <p className="text-[13px] text-text-secondary leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Så fungerar det ── */}
      <section className="py-20 bg-surface-subtle border-t border-border-light">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-14"
          >
            <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-4">Så fungerar det</span>
            <h2 className="text-3xl font-extrabold text-text-primary tracking-tight">
              Tre steg — klart
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {steps.map(({ num, icon: Icon, title, desc }, i) => (
              <motion.div
                key={num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                className="bg-white rounded-2xl border border-border-light p-7 text-center shadow-sm"
              >
                <div className="inline-flex w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 items-center justify-center mb-4 shadow-md">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <span className="block text-[10px] font-bold tracking-widest uppercase text-brand-indigo mb-2">{num}</span>
                <h3 className="text-[16px] font-bold text-text-primary mb-2">{title}</h3>
                <p className="text-[13px] text-text-secondary leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Vad får du ut? ── */}
      <section className="py-20 bg-white border-t border-border-light">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-12"
          >
            <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-4">Resultatet</span>
            <h2 className="text-3xl font-extrabold text-text-primary tracking-tight">Vad får du ut?</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-3xl mx-auto">
            {[
              { emoji: '⏳', title: 'Sparar tid', desc: 'Istället för att lägga en timme på att formulera ett brev — klart på 2 minuter.' },
              { emoji: '⚖️', title: 'Färdigt juridiskt språk', desc: 'Rätt ton, rätt lagstöd, rätt struktur. AI känner till konsumentlagstiftningen.' },
              { emoji: '😌', title: 'Mindre stress', desc: 'Slipp känslan av att inte veta hur man ska göra. Klagobot guidar dig igenom.' },
            ].map(({ emoji, title, desc }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="bg-surface-subtle rounded-2xl border border-border-light p-7 text-center shadow-sm"
              >
                <span className="text-3xl mb-4 block" aria-hidden="true">{emoji}</span>
                <h3 className="text-[16px] font-bold text-text-primary mb-2">{title}</h3>
                <p className="text-[13px] text-text-secondary leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 bg-surface-subtle border-t border-border-light">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <h2 className="text-3xl font-extrabold text-text-primary tracking-tight mb-4">
              Redo att testa?
            </h2>
            <p className="text-text-secondary mb-8 max-w-md mx-auto leading-relaxed">
              Klagobot är under aktiv utveckling. Anmäl intresse — vi meddelar dig när tjänsten är tillgänglig.
            </p>
            <a href="https://klagobot.se" target="_blank" rel="noopener noreferrer">
              <button className="group btn-glow relative overflow-hidden bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-600 text-white font-semibold px-8 py-3.5 rounded-xl text-sm inline-flex items-center gap-2">
                Testa Klagobot direkt
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </button>
            </a>
            <p className="text-xs text-text-muted mt-2">Färdigt brev på 2–5 minuter · Redo att skickas direkt</p>
          </motion.div>
        </div>
      </section>

      <CorporateFooter />
    </div>
  );
};

export default KlagobotPage;
